import { useState, useEffect, createContext, useContext, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./components/ui/dialog";
import { Progress } from "./components/ui/progress";
import { ScrollArea } from "./components/ui/scroll-area";
import { Badge } from "./components/ui/badge";
import { Separator } from "./components/ui/separator";
import { Switch } from "./components/ui/switch";
import { Label } from "./components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { 
  Menu, X, Heart, BookOpen, Users, ShoppingBag, Home, User, 
  ChevronLeft, ChevronRight, Flame, Star, Quote, Check, 
  Lock, Sparkles, LogOut, ArrowRight, Send, Loader2, Download,
  Bell, Mail, Wind, Play, Pause, RotateCcw, Settings,
  Clock, Volume2, Headphones, Sun, MessageCircle, Award,
  Trophy, ScrollText, Shield, Brain, Share2, Twitter, Facebook,
  Cloud, Anchor, Activity, Copy
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);
const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem("token"));

  useEffect(() => {
    if (token) {
      axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => { setUser(res.data); setLoading(false); })
        .catch(() => { localStorage.removeItem("token"); setToken(null); setLoading(false); });
    } else { setLoading(false); }
  }, [token]);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem("token", res.data.token);
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  const register = async (name, email, password) => {
    const res = await axios.post(`${API}/auth/register`, { name, email, password });
    localStorage.setItem("token", res.data.token);
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  const logout = () => { localStorage.removeItem("token"); setToken(null); setUser(null); };
  const refreshUser = async () => { if (token) { const res = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } }); setUser(res.data); } };

  return <AuthContext.Provider value={{ user, token, loading, login, register, logout, refreshUser }}>{children}</AuthContext.Provider>;
};

// Components
const Logo = ({ className = "w-12 h-12" }) => (
  <img src="https://customer-assets.emergentagent.com/job_c761442d-6cf0-46c2-a93c-070adbdc0a4f/artifacts/cymj42fe_BCO.da1b939b-a3b6-473e-b578-d485f0de3e57.png" alt="TriFire Legacy 28" className={className} />
);

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();

  const navLinks = [
    { path: "/", label: "Home", icon: Home },
    { path: "/journal", label: "Journal", icon: BookOpen },
    { path: "/heal", label: "Heal", icon: Sparkles },
    { path: "/journey", label: "Journey", icon: Trophy },
    { path: "/milestones", label: "Milestones", icon: Award },
    { path: "/community", label: "Community", icon: Users },
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <Logo className="w-10 h-10" />
            <span className="font-semibold text-lg hidden sm:block flame-text">TriFire Rising</span>
          </Link>
          <div className="hidden md:flex items-center gap-4">
            {navLinks.map(link => (
              <Link key={link.path} to={link.path} className={`nav-link flex items-center gap-1.5 text-sm font-medium ${location.pathname === link.path ? "text-orange-400" : "text-gray-300 hover:text-white"}`}>
                <link.icon className="w-4 h-4" />{link.label}
              </Link>
            ))}
          </div>
          <div className="hidden md:flex items-center gap-4">
            {user ? (
              <>
                <Link to="/settings" className="text-gray-400 hover:text-white"><Settings className="w-5 h-5" /></Link>
                <Link to="/dashboard" className="flex items-center gap-2 text-sm text-gray-300 hover:text-white"><User className="w-4 h-4" />{user.name}</Link>
                <Button variant="ghost" size="sm" onClick={logout}><LogOut className="w-4 h-4" /></Button>
              </>
            ) : <Link to="/auth"><Button className="btn-flame text-white">Sign In</Button></Link>}
          </div>
          <button className="md:hidden p-2" onClick={() => setIsOpen(!isOpen)}>{isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}</button>
        </div>
      </div>
      {isOpen && (
        <div className="md:hidden glass border-t border-orange-500/20 px-4 py-4 space-y-3">
          {navLinks.map(link => <Link key={link.path} to={link.path} className="flex items-center gap-3 py-2 text-gray-300" onClick={() => setIsOpen(false)}><link.icon className="w-5 h-5" />{link.label}</Link>)}
          <Separator className="bg-orange-500/20" />
          {user ? (
            <>
              <Link to="/settings" className="flex items-center gap-3 py-2 text-gray-300" onClick={() => setIsOpen(false)}><Settings className="w-5 h-5" />Settings</Link>
              <button onClick={() => { logout(); setIsOpen(false); }} className="flex items-center gap-3 py-2 text-gray-300"><LogOut className="w-5 h-5" />Sign Out</button>
            </>
          ) : <Link to="/auth" onClick={() => setIsOpen(false)}><Button className="w-full btn-flame text-white">Sign In</Button></Link>}
        </div>
      )}
    </nav>
  );
};

const DailyPromptCard = () => {
  const [prompt, setPrompt] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { axios.get(`${API}/daily-prompt`).then(res => setPrompt(res.data)); }, []);
  if (!prompt) return null;
  return (
    <Card className="bg-gradient-to-br from-orange-500/20 to-purple-500/20 border-orange-500/30">
      <CardContent className="pt-6">
        <div className="flex items-center gap-2 mb-3"><Sun className="w-5 h-5 text-orange-400" /><span className="text-sm text-orange-400 font-medium">Today's Healing Prompt</span></div>
        <p className="text-xl text-white mb-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>"{prompt.prompt}"</p>
        <div className="flex items-center justify-between">
          <Badge variant="outline" className="border-orange-500/50 text-orange-300">{prompt.section}</Badge>
          <Button variant="ghost" size="sm" onClick={() => navigate("/journal")} className="text-orange-400">Open Journal <ArrowRight className="w-4 h-4 ml-1" /></Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Audio Player for Meditations
const AudioMeditationPlayer = ({ meditationId, title }) => {
  const [loading, setLoading] = useState(false);
  const [audioSrc, setAudioSrc] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);

  const loadAudio = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/audio/meditation/${meditationId}`);
      setAudioSrc(`data:audio/mp3;base64,${res.data.audio_base64}`);
    } catch (err) {
      toast.error("Failed to load audio");
    }
    setLoading(false);
  };

  const togglePlay = () => {
    if (!audioSrc) {
      loadAudio();
      return;
    }
    if (isPlaying) {
      audioRef.current?.pause();
    } else {
      audioRef.current?.play();
    }
    setIsPlaying(!isPlaying);
  };

  useEffect(() => {
    if (audioSrc && audioRef.current) {
      audioRef.current.play();
      setIsPlaying(true);
    }
  }, [audioSrc]);

  return (
    <div className="flex items-center gap-4 p-4 bg-background/50 rounded-lg">
      <Button onClick={togglePlay} disabled={loading} className="w-12 h-12 rounded-full btn-flame text-white p-0" data-testid={`play-${meditationId}`}>
        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
      </Button>
      <div className="flex-1">
        <p className="font-medium">{title}</p>
        <p className="text-sm text-gray-500">{audioSrc ? (isPlaying ? "Playing..." : "Paused") : "Click to generate audio"}</p>
      </div>
      {audioSrc && <audio ref={audioRef} src={audioSrc} onEnded={() => setIsPlaying(false)} />}
    </div>
  );
};

// Breathing Timer
const BreathingTimer = ({ exercise, onComplete }) => {
  const [isActive, setIsActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [timeLeft, setTimeLeft] = useState(0);
  const [cycle, setCycle] = useState(1);
  const intervalRef = useRef(null);
  const steps = exercise.steps;
  const totalCycles = exercise.cycles;

  useEffect(() => {
    if (isActive && timeLeft > 0) {
      intervalRef.current = setInterval(() => setTimeLeft(t => t - 1), 1000);
    } else if (isActive && timeLeft === 0) {
      if (currentStep < steps.length - 1) { setCurrentStep(s => s + 1); setTimeLeft(steps[currentStep + 1].duration); }
      else if (cycle < totalCycles) { setCycle(c => c + 1); setCurrentStep(0); setTimeLeft(steps[0].duration); }
      else { setIsActive(false); onComplete?.(); }
    }
    return () => clearInterval(intervalRef.current);
  }, [isActive, timeLeft, currentStep, cycle, steps, totalCycles, onComplete]);

  const start = () => { setIsActive(true); setCurrentStep(0); setCycle(1); setTimeLeft(steps[0].duration); };
  const currentStepData = steps[currentStep];
  
  return (
    <div className="text-center py-8">
      <div className={`w-40 h-40 mx-auto mb-6 rounded-full flex items-center justify-center transition-all duration-1000 ${isActive ? (currentStepData?.action === "inhale" ? "scale-110 bg-blue-500/20" : currentStepData?.action === "exhale" ? "scale-90 bg-orange-500/20" : "scale-100 bg-purple-500/20") : "bg-gray-800/50"}`}>
        {isActive ? <><p className="text-2xl font-light mb-1">{currentStepData?.action?.toUpperCase()}</p><p className="text-4xl font-bold">{timeLeft}</p></> : <Wind className="w-12 h-12 text-gray-400" />}
      </div>
      {isActive && <p className="text-gray-400 mb-4">Cycle {cycle}/{totalCycles}</p>}
      <div className="flex justify-center gap-4">
        {!isActive ? <Button onClick={start} className="btn-flame text-white px-8"><Play className="w-5 h-5 mr-2" />Start</Button> : <Button onClick={() => setIsActive(false)} variant="outline" className="border-orange-500 text-orange-400"><Pause className="w-5 h-5 mr-2" />Pause</Button>}
        <Button onClick={() => { setIsActive(false); setCurrentStep(0); setCycle(1); setTimeLeft(0); }} variant="ghost" className="text-gray-400"><RotateCcw className="w-5 h-5" /></Button>
      </div>
    </div>
  );
};

// Share Certificate Modal
const ShareModal = ({ isOpen, onClose, shareText, sectionName }) => {
  const copyToClipboard = () => {
    navigator.clipboard.writeText(shareText);
    toast.success("Copied to clipboard!");
  };

  const shareTwitter = () => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`, '_blank');
  const shareFacebook = () => window.open(`https://www.facebook.com/sharer/sharer.php?quote=${encodeURIComponent(shareText)}`, '_blank');

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-card border-orange-500/30">
        <DialogHeader>
          <DialogTitle className="flame-text">Share Your Achievement</DialogTitle>
          <DialogDescription>Celebrate completing {sectionName}!</DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <p className="p-4 bg-background/50 rounded-lg text-gray-300 mb-4">{shareText}</p>
          <div className="flex gap-3">
            <Button onClick={copyToClipboard} variant="outline" className="flex-1 border-gray-700"><Copy className="w-4 h-4 mr-2" />Copy</Button>
            <Button onClick={shareTwitter} className="flex-1 bg-blue-500 hover:bg-blue-600 text-white"><Twitter className="w-4 h-4 mr-2" />Twitter</Button>
            <Button onClick={shareFacebook} className="flex-1 bg-blue-700 hover:bg-blue-800 text-white"><Facebook className="w-4 h-4 mr-2" />Facebook</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Certificate Modal
const CertificateModal = ({ isOpen, onClose, certificate, section, onShare }) => {
  const downloadCertificate = () => {
    const content = `
╔══════════════════════════════════════════════════════════════════╗
║                    TRIFIRE LEGACY 28 FOUNDATION                  ║
║              ════════════════════════════════════                ║
║                    CERTIFICATE OF COMPLETION                     ║
║              ════════════════════════════════════                ║
║                         This certifies that                      ║
║                           ${certificate?.certificate?.user_name?.padStart(20)}                             ║
║                has successfully completed the                    ║
║                           ${section?.name?.padStart(20)}              ║
║                   section of TriFire Rising                      ║
║              ════════════════════════════════════                ║
║                    "Healing Begins at the Heart"                 ║
║                         ${certificate?.certificate?.earned_at?.slice(0, 10)}                              ║
║              Feed the Flame. Empower the Legacy.                 ║
╚══════════════════════════════════════════════════════════════════╝
`;
    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `trifire-certificate-${section?.section_id}.txt`;
    a.click();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl bg-card border-orange-500/30">
        <DialogHeader>
          <DialogTitle className="text-center flame-text text-2xl">Certificate of Completion</DialogTitle>
        </DialogHeader>
        <div className="py-6">
          <div className="bg-gradient-to-br from-orange-500/10 to-purple-500/10 border border-orange-500/30 rounded-lg p-8 text-center">
            <Award className="w-20 h-20 mx-auto mb-4 text-orange-400" />
            <h3 className="text-2xl font-semibold mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>TriFire Legacy 28</h3>
            <p className="text-gray-400 mb-4">This certifies that</p>
            <p className="text-3xl flame-text mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{certificate?.certificate?.user_name}</p>
            <p className="text-gray-400 mb-2">has completed</p>
            <p className="text-xl text-orange-400 mb-4">{section?.name}</p>
            <p className="text-gray-500 text-sm">{certificate?.certificate?.earned_at?.slice(0, 10)}</p>
          </div>
        </div>
        <div className="flex gap-4 justify-center">
          <Button onClick={downloadCertificate} className="btn-flame text-white"><Download className="w-4 h-4 mr-2" />Download</Button>
          <Button onClick={onShare} variant="outline" className="border-orange-500 text-orange-400"><Share2 className="w-4 h-4 mr-2" />Share</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// PAGES

// Home Page
const HomePage = () => {
  const navigate = useNavigate();
  const features = [
    { icon: BookOpen, title: "150 Guided Pages", desc: "6 healing sections with prompts" },
    { icon: Sparkles, title: "Healing Techniques", desc: "Meditate, breathe, affirm, ground" },
    { icon: Award, title: "Certificates", desc: "Earn & share achievements" },
    { icon: Trophy, title: "Journey Story", desc: "Your complete healing narrative" },
  ];

  return (
    <div className="min-h-screen">
      <section className="relative min-h-screen flex items-center justify-center hero-gradient pt-16">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl" />
        </div>
        <div className="relative z-10 max-w-5xl mx-auto px-4 text-center">
          <Logo className="w-32 h-32 mx-auto mb-8 animate-fade-up" />
          <h1 className="text-4xl sm:text-6xl font-light mb-6 flame-text animate-fade-up animation-delay-100" style={{ fontFamily: "'Cormorant Garamond', serif" }}>TriFire Rising</h1>
          <p className="text-xl text-gray-400 mb-4 animate-fade-up animation-delay-200" style={{ fontFamily: "'Cormorant Garamond', serif" }}>A Mother's Healing Journey</p>
          <p className="text-orange-400 text-lg mb-12 animate-fade-up animation-delay-300">Healing Begins at the Heart</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-up animation-delay-400">
            <Button onClick={() => navigate("/journal")} className="btn-flame text-white text-lg px-8 py-6">Start Your Journey <ArrowRight className="ml-2 w-5 h-5" /></Button>
            <Button onClick={() => navigate("/heal")} variant="outline" className="border-orange-500/50 text-orange-400 text-lg px-8 py-6"><Sparkles className="mr-2 w-5 h-5" />Healing Tools</Button>
          </div>
        </div>
      </section>

      <section className="py-16 px-4"><div className="max-w-2xl mx-auto"><DailyPromptCard /></div></section>

      <section className="py-24 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <Quote className="w-12 h-12 text-orange-500/30 mx-auto mb-6" />
          <p className="text-2xl text-gray-300 leading-relaxed mb-8" style={{ fontFamily: "'Cormorant Garamond', serif" }}>This book is for the mother who kept going when no one saw.</p>
          <div className="decorative-line w-32 mx-auto" />
        </div>
      </section>

      <section className="py-24 px-4 bg-gradient-to-b from-transparent via-orange-500/5 to-transparent">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl text-center mb-16 flame-text" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Your Healing Journey</h2>
          <div className="grid md:grid-cols-4 gap-6">
            {features.map((f, i) => (
              <Card key={i} className="bg-card/50 border-orange-500/10 card-hover">
                <CardContent className="pt-8 text-center">
                  <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-orange-500/10 flex items-center justify-center"><f.icon className="w-7 h-7 text-orange-400" /></div>
                  <h3 className="text-lg font-semibold mb-2">{f.title}</h3>
                  <p className="text-gray-400 text-sm">{f.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <Flame className="w-16 h-16 text-orange-400 mx-auto mb-8" />
          <h2 className="text-3xl mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Feed the Flame. Empower the Legacy.</h2>
          <p className="text-gray-400 text-lg mb-8">You are allowed to go slowly.</p>
          <Button onClick={() => navigate("/shop")} className="btn-flame text-white text-lg px-8 py-6"><ShoppingBag className="mr-2 w-5 h-5" />Get Your Journal</Button>
        </div>
      </section>

      <footer className="py-12 px-4 border-t border-orange-500/10">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3"><Logo className="w-8 h-8" /><span className="text-gray-400">TriFire Legacy 28 Foundation</span></div>
          <p className="text-gray-500 text-sm">© 2024 TriFire Legacy 28</p>
        </div>
      </footer>
    </div>
  );
};

// Heal Page (Techniques)
const HealPage = () => {
  const [techniques, setTechniques] = useState([]);
  const [selected, setSelected] = useState(null);
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [breathingExercises, setBreathingExercises] = useState([]);
  const [showComplete, setShowComplete] = useState(false);

  useEffect(() => {
    axios.get(`${API}/healing/techniques`).then(res => setTechniques(res.data));
    axios.get(`${API}/breathing/exercises`).then(res => setBreathingExercises(res.data));
  }, []);

  const getIcon = (icon) => {
    const icons = { sparkles: Sparkles, cloud: Cloud, heart: Heart, anchor: Anchor, wind: Wind, activity: Activity };
    return icons[icon] || Sparkles;
  };

  if (selectedExercise) {
    const exercise = breathingExercises.find(e => e.id === selectedExercise);
    if (exercise) {
      return (
        <div className="min-h-screen pt-20 px-4">
          <div className="max-w-2xl mx-auto py-12">
            <Button variant="ghost" onClick={() => { setSelectedExercise(null); setShowComplete(false); }} className="mb-6 text-gray-400"><ChevronLeft className="w-5 h-5 mr-1" />Back</Button>
            <Card className="bg-card/50 border-orange-500/20">
              <CardHeader className="text-center">
                <CardTitle className="text-2xl flame-text">{exercise.name}</CardTitle>
                <CardDescription>{exercise.description}</CardDescription>
              </CardHeader>
              <CardContent>
                {showComplete ? (
                  <div className="text-center py-12">
                    <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-500/20 flex items-center justify-center"><Check className="w-10 h-10 text-green-400" /></div>
                    <h3 className="text-2xl mb-4 text-green-400">Beautiful Work</h3>
                    <p className="text-gray-500 italic mb-8">"You are allowed to go slowly."</p>
                    <Button onClick={() => setShowComplete(false)} className="btn-flame text-white">Practice Again</Button>
                  </div>
                ) : <BreathingTimer exercise={exercise} onComplete={() => setShowComplete(true)} />}
              </CardContent>
            </Card>
          </div>
        </div>
      );
    }
  }

  if (selected) {
    return (
      <div className="min-h-screen pt-20 px-4">
        <div className="max-w-4xl mx-auto py-12">
          <Button variant="ghost" onClick={() => setSelected(null)} className="mb-6 text-gray-400"><ChevronLeft className="w-5 h-5 mr-1" />Back to Techniques</Button>
          <div className="text-center mb-8">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: `${selected.color}20` }}>
              {(() => { const Icon = getIcon(selected.icon); return <Icon className="w-10 h-10" style={{ color: selected.color }} />; })()}
            </div>
            <h1 className="text-3xl flame-text mb-2">{selected.name}</h1>
            <p className="text-gray-400">{selected.description}</p>
          </div>
          
          <div className="grid gap-4">
            {selected.exercises.map(ex => (
              <Card key={ex.id} className="bg-card/50 border-orange-500/10 card-hover">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold mb-1">{ex.name}</h3>
                      <p className="text-gray-400 text-sm mb-2">{ex.description}</p>
                      <Badge variant="outline" className="text-gray-500">{ex.duration}</Badge>
                    </div>
                    {selected.id === "breathe" ? (
                      <Button onClick={() => setSelectedExercise(ex.id)} className="btn-flame text-white"><Play className="w-4 h-4 mr-2" />Start</Button>
                    ) : selected.id === "meditate" || selected.id === "affirm" || selected.id === "vent" || selected.id === "ground" ? (
                      <AudioMeditationPlayer meditationId={ex.id} title={ex.name} />
                    ) : (
                      <Button variant="outline" className="border-orange-500 text-orange-400"><Play className="w-4 h-4 mr-2" />Begin</Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 px-4">
      <div className="max-w-6xl mx-auto py-12">
        <div className="text-center mb-12">
          <Sparkles className="w-16 h-16 text-orange-400 mx-auto mb-4" />
          <h1 className="text-4xl mb-4 flame-text" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Healing Techniques</h1>
          <p className="text-gray-400 text-lg">Choose what your soul needs today</p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {techniques.map(tech => {
            const Icon = getIcon(tech.icon);
            return (
              <Card key={tech.id} className="bg-card/50 border-orange-500/10 card-hover cursor-pointer" onClick={() => setSelected(tech)} data-testid={`technique-${tech.id}`}>
                <CardContent className="pt-8 text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: `${tech.color}20` }}>
                    <Icon className="w-8 h-8" style={{ color: tech.color }} />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">{tech.name}</h3>
                  <p className="text-gray-400 text-sm mb-4">{tech.description}</p>
                  <Badge variant="outline" className="text-gray-500">{tech.duration}</Badge>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Journey Page
const JourneyPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [sectionProgress, setSectionProgress] = useState([]);
  const [journeyStory, setJourneyStory] = useState(null);
  const [selectedCert, setSelectedCert] = useState(null);
  const [certData, setCertData] = useState(null);
  const [showShare, setShowShare] = useState(false);
  const [shareData, setShareData] = useState({ text: "", section: "" });
  const [claiming, setClaiming] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!user) { navigate("/auth"); return; }
    fetchProgress();
    if (user.has_digital_access) fetchStory();
  }, [user, token, navigate]);

  const fetchProgress = async () => {
    const res = await axios.get(`${API}/journal/section-progress`, { headers: { Authorization: `Bearer ${token}` } });
    setSectionProgress(res.data);
  };

  const fetchStory = async () => {
    const res = await axios.get(`${API}/journal/journey-story`, { headers: { Authorization: `Bearer ${token}` } });
    setJourneyStory(res.data);
  };

  const claimCertificate = async (sectionId) => {
    setClaiming(sectionId);
    try {
      await axios.post(`${API}/journal/claim-certificate/${sectionId}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Certificate earned!");
      fetchProgress();
      viewCertificate(sectionId);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    setClaiming(null);
  };

  const viewCertificate = async (sectionId) => {
    const res = await axios.get(`${API}/journal/certificate/${sectionId}`, { headers: { Authorization: `Bearer ${token}` } });
    setCertData(res.data);
    setSelectedCert(sectionProgress.find(s => s.section_id === sectionId));
  };

  const handleShare = () => {
    setShareData({ text: certData?.share_text || "", section: selectedCert?.name || "" });
    setShowShare(true);
  };

  const downloadStory = async () => {
    setDownloading(true);
    try {
      const response = await axios.get(`${API}/journal/journey-story/download`, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `my-healing-journey.txt`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Downloaded!");
    } catch (err) { toast.error("Failed"); }
    setDownloading(false);
  };

  const getIcon = (icon) => ({ shield: Shield, heart: Heart, brain: Brain, sparkles: Sparkles, users: Users, flame: Flame }[icon] || BookOpen);

  if (!user) return null;

  return (
    <div className="min-h-screen pt-20 px-4">
      <div className="max-w-6xl mx-auto py-12">
        <div className="text-center mb-12">
          <Trophy className="w-16 h-16 text-orange-400 mx-auto mb-4" />
          <h1 className="text-4xl mb-4 flame-text" style={{ fontFamily: "'Cormorant Garamond', serif" }}>My Healing Journey</h1>
        </div>

        <Card className="bg-gradient-to-br from-orange-500/10 to-purple-500/10 border-orange-500/30 mb-8">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div><h2 className="text-2xl font-semibold mb-2">Your Progress</h2><p className="text-gray-400">{sectionProgress.filter(s => s.certificate_earned).length}/6 sections complete</p></div>
              <div className="flex items-center gap-3">
                {sectionProgress.map(s => { const Icon = getIcon(s.icon); return <div key={s.section_id} className={`w-10 h-10 rounded-full flex items-center justify-center ${s.certificate_earned ? 'bg-orange-500' : 'bg-gray-700'}`}><Icon className={`w-5 h-5 ${s.certificate_earned ? 'text-white' : 'text-gray-400'}`} /></div>; })}
              </div>
            </div>
          </CardContent>
        </Card>

        <h2 className="text-2xl mb-6">Section Certificates</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {sectionProgress.map(section => {
            const Icon = getIcon(section.icon);
            return (
              <Card key={section.section_id} className={`bg-card/50 border-l-4 ${section.certificate_earned ? 'border-orange-500' : 'border-gray-700'}`}>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: `${section.color}20` }}><Icon className="w-5 h-5" style={{ color: section.color }} /></div>
                      <div><h3 className="font-semibold">{section.name}</h3><p className="text-xs text-gray-500">{section.completed_prompts}/{section.total_prompts}</p></div>
                    </div>
                    {section.certificate_earned && <Award className="w-6 h-6 text-orange-400" />}
                  </div>
                  <Progress value={section.progress_percent} className="h-2 mb-4" />
                  {section.certificate_earned ? (
                    <Button onClick={() => viewCertificate(section.section_id)} variant="outline" className="w-full border-orange-500 text-orange-400"><Award className="w-4 h-4 mr-2" />View & Share</Button>
                  ) : section.is_complete ? (
                    <Button onClick={() => claimCertificate(section.section_id)} disabled={claiming === section.section_id} className="w-full btn-flame text-white">{claiming === section.section_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trophy className="w-4 h-4 mr-2" />}Claim Certificate</Button>
                  ) : (
                    <Button onClick={() => navigate("/journal")} variant="outline" className="w-full border-gray-700 text-gray-400">Continue</Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        <Card className="bg-card/50 border-orange-500/20">
          <CardHeader><CardTitle className="flex items-center gap-2"><ScrollText className="w-6 h-6 text-orange-400" />My Journey Story</CardTitle></CardHeader>
          <CardContent>
            {user.has_digital_access ? (
              <>
                {journeyStory && (
                  <div className="flex items-center justify-between mb-6 p-4 bg-background/50 rounded-lg">
                    <div><p className="text-sm text-gray-400">Reflections</p><p className="text-2xl font-bold text-orange-400">{journeyStory.total_entries}</p></div>
                    <div><p className="text-sm text-gray-400">Completed</p><p className="text-2xl font-bold text-orange-400">{journeyStory.sections_completed}/6</p></div>
                  </div>
                )}
                <Button onClick={downloadStory} disabled={downloading} className="w-full btn-flame text-white">{downloading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}Download My Story</Button>
              </>
            ) : (
              <div className="text-center py-8"><Lock className="w-12 h-12 text-gray-500 mx-auto mb-4" /><p className="text-gray-400 mb-4">Purchase to unlock</p><Button onClick={() => navigate("/shop")} className="btn-flame text-white">Unlock</Button></div>
            )}
          </CardContent>
        </Card>

        <CertificateModal isOpen={!!selectedCert} onClose={() => { setSelectedCert(null); setCertData(null); }} certificate={certData} section={selectedCert} onShare={handleShare} />
        <ShareModal isOpen={showShare} onClose={() => setShowShare(false)} shareText={shareData.text} sectionName={shareData.section} />
      </div>
    </div>
  );
};

// Milestones Page
const MilestonesPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [milestones, setMilestones] = useState([]);
  const [showPost, setShowPost] = useState(false);
  const [certificates, setCertificates] = useState([]);
  const [selectedSection, setSelectedSection] = useState("");
  const [message, setMessage] = useState("");
  const [posting, setPosting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/milestones`).then(res => setMilestones(res.data));
    if (token) {
      axios.get(`${API}/journal/section-progress`, { headers: { Authorization: `Bearer ${token}` } }).then(res => setCertificates(res.data.filter(s => s.certificate_earned)));
    }
  }, [token]);

  const postMilestone = async () => {
    if (!selectedSection) { toast.error("Select a section"); return; }
    setPosting(true);
    try {
      const res = await axios.post(`${API}/milestones`, { section_id: selectedSection, message, is_public: true }, { headers: { Authorization: `Bearer ${token}` } });
      setMilestones([res.data, ...milestones]);
      setShowPost(false);
      setMessage("");
      setSelectedSection("");
      toast.success("Milestone shared!");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    setPosting(false);
  };

  const likeMilestone = async (id) => {
    if (!user) { toast.error("Please sign in"); return; }
    await axios.post(`${API}/milestones/${id}/like`, {}, { headers: { Authorization: `Bearer ${token}` } });
    setMilestones(milestones.map(m => m.id === id ? { ...m, likes: m.likes + 1 } : m));
  };

  return (
    <div className="min-h-screen pt-20 px-4">
      <div className="max-w-3xl mx-auto py-12">
        <div className="text-center mb-12">
          <Award className="w-16 h-16 text-orange-400 mx-auto mb-4" />
          <h1 className="text-4xl mb-4 flame-text" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Healing Milestones</h1>
          <p className="text-gray-400">Celebrate with mothers on their journey</p>
        </div>

        {user && certificates.length > 0 && (
          <Card className="bg-card/50 border-orange-500/20 mb-8">
            <CardContent className="pt-6">
              {showPost ? (
                <div className="space-y-4">
                  <Select value={selectedSection} onValueChange={setSelectedSection}>
                    <SelectTrigger className="bg-background/50"><SelectValue placeholder="Select achievement to share" /></SelectTrigger>
                    <SelectContent>{certificates.map(c => <SelectItem key={c.section_id} value={c.section_id}>{c.name} Certificate</SelectItem>)}</SelectContent>
                  </Select>
                  <Textarea value={message} onChange={e => setMessage(e.target.value)} placeholder="Share how this milestone feels (optional)" className="bg-background/50" />
                  <div className="flex gap-3">
                    <Button onClick={postMilestone} disabled={posting} className="flex-1 btn-flame text-white">{posting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4 mr-2" />}Share Milestone</Button>
                    <Button onClick={() => setShowPost(false)} variant="outline">Cancel</Button>
                  </div>
                </div>
              ) : (
                <Button onClick={() => setShowPost(true)} className="w-full btn-flame text-white"><Trophy className="w-4 h-4 mr-2" />Share Your Achievement</Button>
              )}
            </CardContent>
          </Card>
        )}

        <div className="space-y-4">
          {milestones.length === 0 ? (
            <div className="text-center py-12 text-gray-500"><Award className="w-12 h-12 mx-auto mb-4 opacity-50" /><p>No milestones shared yet. Be the first!</p></div>
          ) : milestones.map(m => (
            <Card key={m.id} className="bg-card/50 border-orange-500/10">
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-orange-500/20 flex items-center justify-center shrink-0"><Trophy className="w-6 h-6 text-orange-400" /></div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-semibold">{m.user_name}</span>
                      <Badge className="bg-orange-500/20 text-orange-300 text-xs">{m.section_name}</Badge>
                    </div>
                    {m.message && <p className="text-gray-300 mb-3">{m.message}</p>}
                    <div className="flex items-center justify-between">
                      <span className="text-gray-500 text-sm">{new Date(m.created_at).toLocaleDateString()}</span>
                      <button onClick={() => likeMilestone(m.id)} className="flex items-center gap-2 text-gray-400 hover:text-orange-400">
                        <Heart className={`w-4 h-4 ${m.likes > 0 ? "fill-orange-400 text-orange-400" : ""}`} /><span>{m.likes}</span>
                      </button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

// Settings Page
const SettingsPage = () => {
  const { user, token, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [settings, setSettings] = useState({ daily_prompt_email: false, daily_prompt_sms: false, phone: "", prompt_time: "08:00" });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    if (!user) { navigate("/auth"); return; }
    axios.get(`${API}/notifications/settings`, { headers: { Authorization: `Bearer ${token}` } }).then(res => setSettings(res.data));
  }, [user, token, navigate]);

  const saveSettings = async () => {
    setSaving(true);
    await axios.put(`${API}/notifications/settings`, settings, { headers: { Authorization: `Bearer ${token}` } });
    toast.success("Saved!");
    refreshUser();
    setSaving(false);
  };

  const testNotification = async () => {
    setTesting(true);
    await axios.post(`${API}/notifications/test`, {}, { headers: { Authorization: `Bearer ${token}` } });
    toast.success("Test notification sent! (Check logs for mock)");
    setTesting(false);
  };

  if (!user) return null;

  const times = [];
  for (let h = 5; h <= 22; h++) { times.push(`${h.toString().padStart(2, '0')}:00`); times.push(`${h.toString().padStart(2, '0')}:30`); }

  return (
    <div className="min-h-screen pt-20 px-4">
      <div className="max-w-2xl mx-auto py-12">
        <h1 className="text-3xl font-semibold mb-8 flame-text">Settings</h1>

        <Card className="bg-card/50 border-orange-500/20 mb-6">
          <CardHeader><CardTitle className="flex items-center gap-2"><Bell className="w-5 h-5 text-orange-400" />Daily Prompts</CardTitle><CardDescription>Receive healing prompts via email or text</CardDescription></CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3"><Mail className="w-5 h-5 text-gray-400" /><div><Label>Email</Label><p className="text-sm text-gray-500">{user.email}</p></div></div>
              <Switch checked={settings.daily_prompt_email} onCheckedChange={v => setSettings({ ...settings, daily_prompt_email: v })} />
            </div>
            <Separator className="bg-orange-500/10" />
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3"><MessageCircle className="w-5 h-5 text-gray-400" /><div><Label>SMS</Label><p className="text-sm text-gray-500">Text messages</p></div></div>
              <Switch checked={settings.daily_prompt_sms} onCheckedChange={v => setSettings({ ...settings, daily_prompt_sms: v })} />
            </div>
            {settings.daily_prompt_sms && (
              <Input type="tel" placeholder="+1 (555) 123-4567" value={settings.phone || ""} onChange={e => setSettings({ ...settings, phone: e.target.value })} className="bg-background/50" />
            )}
            <Separator className="bg-orange-500/10" />
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3"><Clock className="w-5 h-5 text-gray-400" /><Label>Preferred Time</Label></div>
              <Select value={settings.prompt_time} onValueChange={v => setSettings({ ...settings, prompt_time: v })}>
                <SelectTrigger className="w-32 bg-background/50"><SelectValue /></SelectTrigger>
                <SelectContent>{times.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex gap-3">
              <Button onClick={saveSettings} disabled={saving} className="flex-1 btn-flame text-white">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}Save</Button>
              <Button onClick={testNotification} disabled={testing} variant="outline" className="border-orange-500 text-orange-400">{testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}Test</Button>
            </div>
            <p className="text-xs text-gray-500 text-center">Note: Email/SMS are in demo mode. Add real credentials to enable.</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Auth Page
const AuthPage = () => {
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, register, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => { if (user) navigate("/dashboard"); }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      mode === "login" ? await login(email, password) : await register(name, email, password);
      toast.success(mode === "login" ? "Welcome back!" : "Account created!");
      navigate("/dashboard");
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 pt-20">
      <Card className="w-full max-w-md bg-card/80 border-orange-500/20">
        <CardHeader className="text-center">
          <Logo className="w-16 h-16 mx-auto mb-4" />
          <CardTitle className="text-2xl flame-text">{mode === "login" ? "Welcome Back" : "Begin Your Journey"}</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === "register" && <Input placeholder="Your name" value={name} onChange={e => setName(e.target.value)} required className="bg-background/50" />}
            <Input type="email" placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} required className="bg-background/50" />
            <Input type="password" placeholder="Password" value={password} onChange={e => setPassword(e.target.value)} required className="bg-background/50" />
            <Button type="submit" className="w-full btn-flame text-white" disabled={loading}>{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : mode === "login" ? "Sign In" : "Create Account"}</Button>
          </form>
          <div className="mt-6 text-center"><button onClick={() => setMode(mode === "login" ? "register" : "login")} className="text-orange-400 hover:underline text-sm">{mode === "login" ? "Need an account? Sign up" : "Have an account? Sign in"}</button></div>
        </CardContent>
      </Card>
    </div>
  );
};

// Dashboard
const DashboardPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [progress, setProgress] = useState(null);
  const [sectionProgress, setSectionProgress] = useState([]);

  useEffect(() => {
    if (!user) { navigate("/auth"); return; }
    axios.get(`${API}/journal/progress`, { headers: { Authorization: `Bearer ${token}` } }).then(res => setProgress(res.data));
    axios.get(`${API}/journal/section-progress`, { headers: { Authorization: `Bearer ${token}` } }).then(res => setSectionProgress(res.data));
  }, [user, token, navigate]);

  if (!user) return null;

  return (
    <div className="min-h-screen pt-20 px-4">
      <div className="max-w-6xl mx-auto py-8">
        <h1 className="text-3xl font-semibold mb-2">Welcome, {user.name}</h1>
        <p className="text-gray-400 mb-8">Your healing journey continues.</p>

        <div className="mb-8"><DailyPromptCard /></div>

        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <Card className="bg-card/50 border-orange-500/10">
            <CardContent className="pt-6">
              <span className="text-gray-400 block mb-2">Progress</span>
              <span className="text-4xl font-bold text-orange-400">{progress?.progress_percent || 0}%</span>
              <Progress value={progress?.progress_percent || 0} className="mt-3 h-2" />
            </CardContent>
          </Card>
          <Card className="bg-card/50 border-orange-500/10">
            <CardContent className="pt-6">
              <span className="text-gray-400 block mb-2">Prompts</span>
              <span className="text-4xl font-bold text-orange-400">{progress?.completed_prompts || 0}</span>
              <span className="text-gray-500">/{progress?.total_prompts || 0}</span>
            </CardContent>
          </Card>
          <Card className="bg-card/50 border-orange-500/10">
            <CardContent className="pt-6">
              <span className="text-gray-400 block mb-2">Certificates</span>
              <span className="text-4xl font-bold text-orange-400">{sectionProgress.filter(s => s.certificate_earned).length}</span>
              <span className="text-gray-500">/6</span>
            </CardContent>
          </Card>
          <Card className="bg-card/50 border-orange-500/10">
            <CardContent className="pt-6">
              <span className="text-gray-400 block mb-2">Access</span>
              {user.has_digital_access ? <Badge className="bg-green-500">Full</Badge> : <Badge variant="outline" className="border-orange-500 text-orange-400">Preview</Badge>}
            </CardContent>
          </Card>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <Card className="bg-card/50 border-orange-500/10">
            <CardHeader><CardTitle>Quick Actions</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <Button onClick={() => navigate("/journal")} className="w-full btn-flame text-white"><BookOpen className="mr-2 w-4 h-4" />Open Journal</Button>
              <Button onClick={() => navigate("/heal")} variant="outline" className="w-full border-orange-500/50 text-orange-400"><Sparkles className="mr-2 w-4 h-4" />Healing Tools</Button>
              <Button onClick={() => navigate("/journey")} variant="outline" className="w-full border-orange-500/50 text-orange-400"><Trophy className="mr-2 w-4 h-4" />My Journey</Button>
            </CardContent>
          </Card>
          <Card className="bg-card/50 border-orange-500/10">
            <CardHeader><CardTitle>Section Progress</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-3">
                {sectionProgress.slice(0, 4).map(s => (
                  <div key={s.section_id} className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: `${s.color}20` }}>{s.certificate_earned && <Check className="w-4 h-4" style={{ color: s.color }} />}</div>
                    <div className="flex-1"><p className="text-sm">{s.name}</p><Progress value={s.progress_percent} className="h-1.5" /></div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

// Journal Page (simplified)
const JournalPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [pages, setPages] = useState([]);
  const [entries, setEntries] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [content, setContent] = useState("");
  const [saving, setSaving] = useState(false);
  const [sections, setSections] = useState([]);

  useEffect(() => {
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    Promise.all([axios.get(`${API}/journal/pages`, { headers }), axios.get(`${API}/journal/sections`)]).then(([p, s]) => { setPages(p.data); setSections(s.data); });
    if (token) axios.get(`${API}/journal/entries`, { headers }).then(res => { const m = {}; res.data.forEach(e => m[e.page_number] = e.content); setEntries(m); });
  }, [token]);

  useEffect(() => { setContent(entries[currentPage] || ""); }, [currentPage, entries]);

  const currentPageData = pages.find(p => p.page === currentPage);
  const hasAccess = user?.has_digital_access || currentPage <= 15;

  const saveEntry = async () => {
    if (!token) { toast.error("Sign in to save"); return; }
    setSaving(true);
    await axios.post(`${API}/journal/entries`, { page_number: currentPage, content }, { headers: { Authorization: `Bearer ${token}` } });
    setEntries({ ...entries, [currentPage]: content });
    toast.success("Saved!");
    setSaving(false);
  };

  if (!currentPageData) return <div className="min-h-screen flex items-center justify-center pt-20"><Loader2 className="w-8 h-8 animate-spin text-orange-400" /></div>;

  const sectionColors = { "SAFETY & ARRIVAL": "border-green-500", "BODY": "border-orange-500", "MIND": "border-blue-500", "SOUL": "border-purple-500", "POURING INTO EACH OTHER": "border-pink-500", "CLOSING": "border-yellow-500" };

  return (
    <div className="min-h-screen pt-20 px-4">
      <div className="max-w-5xl mx-auto py-8">
        <div className="mb-6 flex gap-2 overflow-x-auto pb-2">
          {sections.map(s => <Button key={s.id} variant="outline" size="sm" onClick={() => setCurrentPage(parseInt(s.pages.split("-")[0]))} className={`whitespace-nowrap ${currentPageData?.section?.includes(s.name.toUpperCase().split(" ")[0]) ? "bg-orange-500/20 border-orange-500" : "border-gray-700"}`}>{s.name}</Button>)}
        </div>

        <Card className={`journal-page min-h-[500px] ${sectionColors[currentPageData.section] || "border-orange-500"} border-l-4`}>
          <CardContent className="p-8">
            <div className="flex items-center justify-between mb-8">
              <Badge variant="outline" className="text-orange-400 border-orange-500/50">{currentPageData.section}</Badge>
              <span className="text-gray-500">Page {currentPage}/150</span>
            </div>

            {!hasAccess ? (
              <div className="text-center py-20">
                <Lock className="w-16 h-16 text-orange-400/50 mx-auto mb-6" />
                <h3 className="text-2xl mb-4">Full Access Required</h3>
                <Button onClick={() => navigate("/shop")} className="btn-flame text-white">Unlock</Button>
              </div>
            ) : currentPageData.silence ? (
              <div className="h-80 flex items-center justify-center">
                <div className="text-center breathe">
                  <p className="text-3xl text-gray-500" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Silence</p>
                  <Button variant="ghost" onClick={() => navigate("/heal")} className="mt-6 text-orange-400"><Wind className="w-4 h-4 mr-2" />Breathing exercise</Button>
                </div>
              </div>
            ) : (
              <div className="space-y-8">
                <div className="text-center">
                  <h2 className="text-3xl mb-4 flame-text" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{currentPageData.title}</h2>
                  {currentPageData.content && <p className="text-xl text-gray-300 whitespace-pre-line" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{currentPageData.content}</p>}
                </div>
                {currentPageData.prompt && (
                  <div>
                    <Textarea value={content} onChange={e => setContent(e.target.value)} placeholder="Write here..." className="journal-textarea min-h-[150px] text-lg" />
                    <div className="flex justify-end mt-4"><Button onClick={saveEntry} disabled={saving} className="btn-flame text-white">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}Save</Button></div>
                  </div>
                )}
              </div>
            )}

            <div className="flex items-center justify-between mt-8 pt-8 border-t border-orange-500/10">
              <Button variant="ghost" onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1}><ChevronLeft className="w-5 h-5" /></Button>
              <Input type="number" min={1} max={user?.has_digital_access ? 150 : 15} value={currentPage} onChange={e => setCurrentPage(Math.min(Math.max(1, parseInt(e.target.value) || 1), user?.has_digital_access ? 150 : 15))} className="w-16 text-center bg-background/50" />
              <Button variant="ghost" onClick={() => setCurrentPage(Math.min(user?.has_digital_access ? 150 : 15, currentPage + 1))} disabled={currentPage >= (user?.has_digital_access ? 150 : 15)}><ChevronRight className="w-5 h-5" /></Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Shop Page
const ShopPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(null);

  useEffect(() => { axios.get(`${API}/shop/products`).then(res => setProducts(res.data)); }, []);

  const buy = async (id) => {
    if (!user) { navigate("/auth"); return; }
    setLoading(id);
    const res = await axios.post(`${API}/checkout/create`, { product_id: id, origin_url: window.location.origin }, { headers: { Authorization: `Bearer ${token}` } });
    window.location.href = res.data.url;
  };

  return (
    <div className="min-h-screen pt-20 px-4">
      <div className="max-w-4xl mx-auto py-12">
        <h1 className="text-4xl text-center mb-12 flame-text" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Shop</h1>
        <div className="grid md:grid-cols-3 gap-6">
          {products.map(p => (
            <Card key={p.id} className={`bg-card/50 border-orange-500/20 ${p.id === "bundle" ? "ring-2 ring-orange-500/50" : ""}`}>
              {p.id === "bundle" && <div className="bg-orange-500 text-center py-2 text-sm font-semibold">Best Value</div>}
              <CardContent className="pt-8 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-orange-500/10 flex items-center justify-center">{p.id === "digital_journal" ? <BookOpen className="w-8 h-8 text-orange-400" /> : p.id === "printed_journal" ? <ShoppingBag className="w-8 h-8 text-orange-400" /> : <Sparkles className="w-8 h-8 text-orange-400" />}</div>
                <h3 className="text-lg font-semibold mb-2">{p.name}</h3>
                <p className="text-gray-400 text-sm mb-4">{p.description}</p>
                <p className="text-3xl font-bold text-orange-400 mb-4">${p.price}</p>
                <Button onClick={() => buy(p.id)} className="w-full btn-flame text-white" disabled={loading === p.id}>{loading === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : "Purchase"}</Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

// Checkout Success
const CheckoutSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const { refreshUser } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking");
  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let attempts = 0;
    const poll = async () => {
      const res = await axios.get(`${API}/checkout/status/${sessionId}`);
      if (res.data.payment_status === "paid") { setStatus("success"); await refreshUser(); }
      else if (attempts < 5) { attempts++; setTimeout(poll, 2000); }
      else setStatus("pending");
    };
    poll();
  }, [sessionId, refreshUser]);

  return (
    <div className="min-h-screen flex items-center justify-center pt-20 px-4">
      <Card className="w-full max-w-md bg-card/80 border-orange-500/20 text-center">
        <CardContent className="pt-12 pb-8">
          {status === "checking" && <><Loader2 className="w-16 h-16 animate-spin text-orange-400 mx-auto mb-6" /><h2 className="text-2xl">Processing...</h2></>}
          {status === "success" && <><div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-500/20 flex items-center justify-center"><Check className="w-10 h-10 text-green-400" /></div><h2 className="text-2xl text-green-400 mb-8">Success!</h2><Button onClick={() => navigate("/journal")} className="w-full btn-flame text-white mb-3">Start Journal</Button><Button onClick={() => navigate("/journey")} variant="outline" className="w-full border-orange-500 text-orange-400"><Trophy className="w-4 h-4 mr-2" />View Journey</Button></>}
          {status === "error" && <><X className="w-16 h-16 text-red-400 mx-auto mb-6" /><h2 className="text-2xl text-red-400 mb-8">Error</h2><Button onClick={() => navigate("/shop")} className="btn-flame text-white">Try Again</Button></>}
        </CardContent>
      </Card>
    </div>
  );
};

// Community
const CommunityPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [content, setContent] = useState("");
  const [anon, setAnon] = useState(true);
  const [posting, setPosting] = useState(false);

  useEffect(() => { axios.get(`${API}/community/posts`).then(res => setPosts(res.data)); }, []);

  const post = async () => {
    if (!user) { navigate("/auth"); return; }
    setPosting(true);
    const res = await axios.post(`${API}/community/posts`, { content, is_anonymous: anon }, { headers: { Authorization: `Bearer ${token}` } });
    setPosts([res.data, ...posts]);
    setContent("");
    toast.success("Posted!");
    setPosting(false);
  };

  const like = async (id) => {
    if (!user) { toast.error("Sign in"); return; }
    await axios.post(`${API}/community/posts/${id}/like`, {}, { headers: { Authorization: `Bearer ${token}` } });
    setPosts(posts.map(p => p.id === id ? { ...p, likes: p.likes + 1 } : p));
  };

  return (
    <div className="min-h-screen pt-20 px-4">
      <div className="max-w-3xl mx-auto py-12">
        <h1 className="text-4xl text-center mb-12 flame-text" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Community</h1>
        <Card className="bg-card/50 border-orange-500/20 mb-8">
          <CardContent className="pt-6">
            <Textarea value={content} onChange={e => setContent(e.target.value)} placeholder="Share..." className="min-h-[80px] bg-background/50 mb-4" />
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2"><input type="checkbox" checked={anon} onChange={e => setAnon(e.target.checked)} /><span className="text-sm text-gray-400">Anonymous</span></label>
              <Button onClick={post} disabled={posting || !content.trim()} className="btn-flame text-white">{posting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}Share</Button>
            </div>
          </CardContent>
        </Card>
        <div className="space-y-4">
          {posts.map(p => (
            <Card key={p.id} className="bg-card/50 border-orange-500/10">
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-orange-500/20 flex items-center justify-center"><span className="text-orange-400 font-semibold">{p.author_name[0]}</span></div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2"><span className="font-semibold">{p.author_name}</span><span className="text-gray-500 text-sm">{new Date(p.created_at).toLocaleDateString()}</span></div>
                    <p className="text-gray-300">{p.content}</p>
                    <button onClick={() => like(p.id)} className="mt-4 flex items-center gap-2 text-gray-400 hover:text-orange-400"><Heart className={`w-4 h-4 ${p.likes > 0 ? "fill-orange-400 text-orange-400" : ""}`} />{p.likes}</button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

// App
function App() {
  return (
    <AuthProvider>
      <div className="App min-h-screen bg-background">
        <BrowserRouter>
          <Navigation />
          <Toaster position="top-right" theme="dark" />
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/auth" element={<AuthPage />} />
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/journal" element={<JournalPage />} />
            <Route path="/heal" element={<HealPage />} />
            <Route path="/journey" element={<JourneyPage />} />
            <Route path="/milestones" element={<MilestonesPage />} />
            <Route path="/shop" element={<ShopPage />} />
            <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
            <Route path="/community" element={<CommunityPage />} />
            <Route path="/settings" element={<SettingsPage />} />
          </Routes>
        </BrowserRouter>
      </div>
    </AuthProvider>
  );
}

export default App;
